<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky tb-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.trialBalance.title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.csv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ $t("finance.trialBalance.printPdf") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field
            v-model="filters.from"
            type="date"
            :label="$t('finance.common.from')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.to"
            type="date"
            :label="$t('finance.common.to')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.code_from"
            :label="$t('finance.trialBalance.codeFrom')"
            hide-details
            clearable
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.code_to"
            :label="$t('finance.trialBalance.codeTo')"
            hide-details
            clearable
            class="fin-field"
          ></v-text-field>
          <v-select
            v-model="filters.max_level"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.trialBalance.maxLevel')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.kind"
            :items="kindOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.coa.kind')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.nature"
            :items="natureOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.coa.nature')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.is_active"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.common.status')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.balance_side"
            :items="balanceSideOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.trialBalance.balanceSide')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-btn small color="primary" @click="loadReport">{{ $t("finance.common.apply") }}</v-btn>
        </div>
      </div>

      <div v-if="hasReport && !report.totals.is_balanced" class="fin-alert-error tb-no-print">
        <v-icon small color="#c62828">warning</v-icon>
        {{ $t("finance.trialBalance.totalsMismatchWarning") }}
        ({{ $t("finance.trialBalance.difference") }}: {{ formatAmount(report.totals.difference) }})
      </div>

      <div v-if="hasReport" class="fin-summary tb-no-print">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.trialBalance.totalDebit") }}</span>
          <span class="fin-num fin-stat-value">{{ formatAmount(report.totals.debit_total) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.trialBalance.totalCredit") }}</span>
          <span class="fin-num fin-stat-value">{{ formatAmount(report.totals.credit_total) }}</span>
        </div>
        <v-chip small :color="report.totals.is_balanced ? 'green' : 'red'" text-color="white">
          <v-icon small class="me-1">{{ report.totals.is_balanced ? "check_circle" : "warning" }}</v-icon>
          {{ report.totals.is_balanced ? $t("finance.trialBalance.totalsMatch") : $t("finance.trialBalance.totalsMismatch") }}
        </v-chip>
        <v-chip v-if="abnormalRowCount" small color="orange" text-color="white">
          <v-icon small class="me-1">warning</v-icon>
          {{ $t("finance.trialBalance.abnormalCount") }}: {{ abnormalRowCount }}
        </v-chip>
      </div>

      <div class="tb-table-wrap fin-table-wrap">
        <table class="tb-tree-table fin-table">
          <thead>
            <tr>
              <th>{{ $t("finance.common.code") }}</th>
              <th class="tb-col-name">{{ $t("finance.common.name") }}</th>
              <th class="fin-num-head">{{ $t("finance.trialBalance.debitMovements") }}</th>
              <th class="fin-num-head">{{ $t("finance.trialBalance.creditMovements") }}</th>
              <th class="fin-num-head">{{ $t("finance.common.balance") }}</th>
              <th>{{ $t("finance.trialBalance.balanceSide") }}</th>
              <th class="tb-col-notes">{{ $t("finance.trialBalance.notes") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row.account.id"
              :class="rowClasses(row)"
              :title="row.account.matches_filters ? '' : $t('finance.trialBalance.contextRow')"
            >
              <td class="fin-code">{{ row.account.code }}</td>
              <td class="tb-col-name">
                <span class="tb-indent" :style="{ 'padding-inline-start': row.depth * 26 + 'px' }">
                  <v-btn
                    v-if="row.hasChildren"
                    icon
                    small
                    class="tb-toggle tb-no-print"
                    @click="toggleNode(row.account.id)"
                  >
                    <v-icon small>{{ isExpanded(row.account.id) ? "remove" : "add" }}</v-icon>
                  </v-btn>
                  <span v-else class="tb-toggle-spacer"></span>
                  <span class="fin-ellipsis" :title="localizedAccountName(row.account)">{{ localizedAccountName(row.account) }}</span>
                </span>
              </td>
              <td class="fin-num">{{ formatAmount(row.account.debit_total) }}</td>
              <td class="fin-num">{{ formatAmount(row.account.credit_total) }}</td>
              <td class="fin-num">{{ formatAmount(row.account.balance) }}</td>
              <td>
                <span class="tb-side" :class="'tb-side-' + row.account.balance_side">
                  {{ formatBalanceSide(row.account.balance_side) }}
                </span>
              </td>
              <td class="tb-col-notes">
                <span v-if="row.account.is_abnormal" class="tb-note">
                  <v-icon small color="deep-orange" class="me-1">warning</v-icon>
                  {{ noteText(row.account) }}
                </span>
              </td>
            </tr>
            <tr v-if="!visibleRows.length && !loading">
              <td colspan="7" class="fin-empty">{{ $t("finance.coa.noResults") }}</td>
            </tr>
          </tbody>
          <tfoot v-if="hasReport">
            <tr class="tb-totals-row">
              <td></td>
              <td>{{ $t("finance.trialBalance.totals") }}</td>
              <td class="fin-num">{{ formatAmount(report.totals.debit_total) }}</td>
              <td class="fin-num">{{ formatAmount(report.totals.credit_total) }}</td>
              <td class="fin-num">{{ formatAmount(Math.abs(report.totals.difference)) }}</td>
              <td :class="report.totals.is_balanced ? 'tb-match-ok' : 'tb-match-bad'">
                {{ report.totals.is_balanced ? $t("finance.trialBalance.totalsMatch") : $t("finance.trialBalance.totalsMismatch") }}
              </td>
              <td>{{ report.totals.is_balanced ? "" : $t("finance.trialBalance.totalsMismatchWarning") }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import { companyNow, companyToday } from "@/util/companyTime";

export default {
  name: "TrialBalance",
  title() {
    return this.$t("finance.trialBalance.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) day on the
    // server-synced clock, not the PC calendar.
    const today = companyToday();

    return {
      loading: false,
      report: null,
      expandedIds: {},
      filters: {
        // Default period: the current fiscal year to date.
        from: today.slice(0, 4) + "-01-01",
        to: today,
        code_from: null,
        code_to: null,
        max_level: null,
        kind: null,
        nature: null,
        is_active: null,
        balance_side: null,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.totals);
    },
    reportRows() {
      return (this.report && this.report.rows) || [];
    },
    rowsById() {
      const map = {};
      this.reportRows.forEach((row) => {
        map[row.id] = row;
      });
      return map;
    },
    childrenByParent() {
      // Server rows arrive in tree order (parents before children, siblings
      // by code), so grouping preserves display order. A parent missing from
      // the filtered payload turns its children into roots.
      const map = {};
      this.reportRows.forEach((row) => {
        const parentId = row.parent_id && this.rowsById[row.parent_id] ? row.parent_id : 0;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(row);
      });
      return map;
    },
    visibleRows() {
      const rows = [];
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          const hasChildren = !!(this.childrenByParent[account.id] || []).length;
          rows.push({ account, depth, hasChildren });
          if (hasChildren && this.expandedIds[account.id]) {
            const nextSeen = Object.assign({}, seen);
            nextSeen[account.id] = true;
            walk(account.id, depth + 1, nextSeen);
          }
        });
      };
      walk(0, 0, {});
      return rows;
    },
    abnormalRowCount() {
      return this.reportRows.filter((row) => row.is_abnormal && row.matches_filters).length;
    },
    levelOptions() {
      const options = [{ text: this.$t("finance.trialBalance.allLevels"), value: null }];
      for (let level = 1; level <= 8; level++) {
        options.push({ text: String(level), value: level });
      }
      return options;
    },
    kindOptions() {
      return [
        { text: this.$t("finance.coa.allKinds"), value: null },
        { text: this.$t("finance.coa.kindMain"), value: "main" },
        { text: this.$t("finance.coa.kindSub"), value: "sub" },
        { text: this.$t("finance.coa.kindClosing"), value: "closing" },
      ];
    },
    natureOptions() {
      return [
        { text: this.$t("finance.coa.allNatures"), value: null },
        { text: this.$t("finance.coa.balanceDebit"), value: "debit" },
        { text: this.$t("finance.coa.balanceCredit"), value: "credit" },
        { text: this.$t("finance.coa.natureBoth"), value: "both" },
        { text: this.$t("finance.coa.natureNone"), value: "none" },
      ];
    },
    statusOptions() {
      return [
        { text: this.$t("finance.common.all"), value: null },
        { text: this.$t("finance.coa.active"), value: 1 },
        { text: this.$t("finance.coa.inactive"), value: 0 },
      ];
    },
    balanceSideOptions() {
      return [
        { text: this.$t("finance.common.all"), value: null },
        { text: this.$t("finance.trialBalance.sideDebit"), value: "debit" },
        { text: this.$t("finance.trialBalance.sideCredit"), value: "credit" },
        { text: this.$t("finance.trialBalance.sideBalanced"), value: "balanced" },
      ];
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    buildParams(extra) {
      const params = { view: "tree" };
      if (this.filters.from) params.from = this.filters.from;
      if (this.filters.to) params.to = this.filters.to;
      if (this.filters.code_from) params.code_from = this.filters.code_from;
      if (this.filters.code_to) params.code_to = this.filters.code_to;
      if (this.filters.max_level) params.max_level = this.filters.max_level;
      if (this.filters.kind) params.kind = this.filters.kind;
      if (this.filters.nature) params.nature = this.filters.nature;
      if (this.filters.is_active !== null && this.filters.is_active !== undefined) {
        params.is_active = this.filters.is_active;
      }
      if (this.filters.balance_side) params.balance_side = this.filters.balance_side;
      return Object.assign(params, extra || {});
    },
    loadReport() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_TRIAL_BALANCE, { params: this.buildParams() })
        .then(
          function (response) {
            this.report = response.data.data || null;
            // Accountants read the whole structure by default.
            this.expandAll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedLoadTrialBalance")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    toggleNode(id) {
      const next = Object.assign({}, this.expandedIds);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.expandedIds = next;
    },
    expandAll() {
      const next = {};
      this.reportRows.forEach((row) => {
        next[row.id] = true;
      });
      this.expandedIds = next;
    },
    collapseAll() {
      this.expandedIds = {};
    },
    rowClasses(row) {
      return {
        "tb-level-1": row.account.level === 1,
        "tb-level-2": row.account.level === 2,
        "tb-parent-row": row.hasChildren,
        "tb-abnormal-row": row.account.is_abnormal,
        "tb-context-row": !row.account.matches_filters,
        "tb-inactive-row": !row.account.is_active,
      };
    },
    localizedAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    formatAmount(value) {
      return this.formatMoney(value);
    },
    formatBalanceSide(side) {
      const map = {
        debit: this.$t("finance.trialBalance.sideDebit"),
        credit: this.$t("finance.trialBalance.sideCredit"),
        balanced: this.$t("finance.trialBalance.sideBalanced"),
      };
      return map[side] || side || "-";
    },
    noteText(account) {
      const map = {
        asset_credit_balance: this.$t("finance.trialBalance.noteAssetCredit"),
        liability_debit_balance: this.$t("finance.trialBalance.noteLiabilityDebit"),
        expense_credit_balance: this.$t("finance.trialBalance.noteExpenseCredit"),
      };
      return (account.note_key && map[account.note_key]) || account.note || "";
    },
    exportFileStamp() {
      // Final-version doc 2026-10-05: company day, not the PC calendar.
      return companyToday();
    },
    periodLabel() {
      const from = this.filters.from || "—";
      const to = this.filters.to || "—";
      return this.$t("finance.common.from") + " " + from + " " + this.$t("finance.common.to") + " " + to;
    },
    downloadCsv() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_TRIAL_BALANCE, {
          params: this.buildParams({ format: "csv" }),
          responseType: "blob",
        })
        .then(
          function (response) {
            const blob = new Blob([response.data], { type: response.headers["content-type"] || "text/csv" });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = "trial-balance-" + this.exportFileStamp() + ".csv";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedDownloadReport")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    // The whole loaded report in tree order (ignores expand state): exports
    // always carry the full filtered report, not whatever is on screen.
    fullTreeRows() {
      const rows = [];
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          rows.push({ account, depth });
          const nextSeen = Object.assign({}, seen);
          nextSeen[account.id] = true;
          walk(account.id, depth + 1, nextSeen);
        });
      };
      walk(0, 0, {});
      return rows;
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      // Real .xlsx built client-side from the loaded report, in tree order
      // with level styling and abnormal-balance highlighting (doc 4.3): the
      // CSV endpoint stays available for raw integrations.
      const rows = this.fullTreeRows();
      const totals = this.report.totals;
      const isArabic = this.$i18n && this.$i18n.locale === "ar";

      import(/* webpackChunkName: "xlsx" */ "xlsx-js-style")
        .then(
          function (module) {
            const XLSX = module.default || module;
            const moneyFormat = "#,##0.00";
            // Stored amounts carry the pre-redenomination scale; exports show
            // the new currency (÷100), same as the money mixin.
            const money = (value) => Number(value || 0) / 100;

            const header = [
              this.$t("finance.common.code"),
              this.$t("finance.common.name"),
              this.$t("finance.coa.level"),
              this.$t("finance.trialBalance.debitMovements"),
              this.$t("finance.trialBalance.creditMovements"),
              this.$t("finance.common.balance"),
              this.$t("finance.trialBalance.balanceSide"),
              this.$t("finance.trialBalance.notes"),
            ];
            const data = rows.map(
              function (row) {
                return [
                  String(row.account.code),
                  this.localizedAccountName(row.account),
                  Number(row.account.level || 1),
                  money(row.account.debit_total),
                  money(row.account.credit_total),
                  money(row.account.balance),
                  this.formatBalanceSide(row.account.balance_side),
                  row.account.is_abnormal ? this.noteText(row.account) : "",
                ];
              }.bind(this)
            );
            const totalsRow = [
              "",
              this.$t("finance.trialBalance.totals"),
              "",
              money(totals.debit_total),
              money(totals.credit_total),
              money(Math.abs(totals.difference)),
              totals.is_balanced
                ? this.$t("finance.trialBalance.totalsMatch")
                : this.$t("finance.trialBalance.totalsMismatch"),
              totals.is_balanced ? "" : this.$t("finance.trialBalance.totalsMismatchWarning"),
            ];

            const ws = XLSX.utils.aoa_to_sheet([header].concat(data, [totalsRow]));
            ws["!cols"] = [
              { wch: 14 },
              { wch: 48 },
              { wch: 8 },
              { wch: 16 },
              { wch: 16 },
              { wch: 16 },
              { wch: 10 },
              { wch: 52 },
            ];

            const thin = { style: "thin", color: { rgb: "9E9E9E" } };
            const borders = { top: thin, bottom: thin, left: thin, right: thin };
            const lastRowIndex = data.length + 1;
            for (let r = 0; r <= lastRowIndex; r++) {
              for (let c = 0; c < header.length; c++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
                if (!cell) {
                  continue;
                }
                if (r === 0) {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "ECEFF1" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  continue;
                }
                if (r === lastRowIndex) {
                  cell.s = {
                    font: { bold: true },
                    fill: {
                      patternType: "solid",
                      fgColor: { rgb: totals.is_balanced ? "E8F5E9" : "FFCDD2" },
                    },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  if (c >= 3 && c <= 5) {
                    cell.z = moneyFormat;
                  }
                  continue;
                }
                const row = rows[r - 1];
                const bold = Number(row.account.level || 1) <= 2;
                cell.s = {
                  font: { bold: bold },
                  border: borders,
                  alignment:
                    c === 1
                      ? { indent: Math.min(row.depth, 15), vertical: "center" }
                      : { vertical: "center" },
                };
                // Abnormal balances stand out in orange (doc 4.1 visual rule).
                if (row.account.is_abnormal) {
                  cell.s.fill = { patternType: "solid", fgColor: { rgb: "FFE0B2" } };
                }
                if (c >= 3 && c <= 5) {
                  cell.z = moneyFormat;
                }
              }
            }

            const wb = XLSX.utils.book_new();
            // Sheet reads right-to-left for the Arabic report.
            wb.Workbook = { Views: [{ RTL: !!isArabic }] };
            XLSX.utils.book_append_sheet(wb, ws, this.$t("finance.trialBalance.pageTitle"));
            XLSX.writeFile(wb, "trial-balance-" + this.exportFileStamp() + ".xlsx");
          }.bind(this)
        )
        .catch(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.failedDownloadReport"), "red");
          }.bind(this)
        );
    },
    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      // Dedicated print document (doc 4.3: print + save-as-PDF): no app
      // chrome, repeated table header per page, RTL-aware, level styling and
      // abnormal highlighting preserved. The browser's "Save as PDF" printer
      // renders the Arabic faithfully.
      const rows = this.fullTreeRows();
      const totals = this.report.totals;
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      const esc = this.escapeHtml;
      const title = this.$t("finance.trialBalance.pageTitle");
      // Final-version doc 2026-10-05: company (Damascus) time, not the PC's.
      const stamp = companyNow().format("YYYY-MM-DD HH:mm:ss");

      const headerCells = [
        this.$t("finance.common.code"),
        this.$t("finance.common.name"),
        this.$t("finance.trialBalance.debitMovements"),
        this.$t("finance.trialBalance.creditMovements"),
        this.$t("finance.common.balance"),
        this.$t("finance.trialBalance.balanceSide"),
        this.$t("finance.trialBalance.notes"),
      ]
        .map(function (label) {
          return "<th>" + esc(label) + "</th>";
        })
        .join("");

      const bodyRows = rows
        .map(
          function (row) {
            const level = Number(row.account.level || 1);
            const classes = [];
            if (level === 1) classes.push("lvl-1");
            if (level === 2) classes.push("lvl-2");
            if (row.account.is_abnormal) classes.push("abnormal");
            return (
              '<tr class="' + classes.join(" ") + '">' +
              '<td class="code">' + esc(row.account.code) + "</td>" +
              '<td><span style="padding-inline-start:' + row.depth * 16 + 'px">' +
              esc(this.localizedAccountName(row.account)) + "</span></td>" +
              '<td class="num">' + esc(this.formatAmount(row.account.debit_total)) + "</td>" +
              '<td class="num">' + esc(this.formatAmount(row.account.credit_total)) + "</td>" +
              '<td class="num">' + esc(this.formatAmount(row.account.balance)) + "</td>" +
              "<td>" + esc(this.formatBalanceSide(row.account.balance_side)) + "</td>" +
              "<td>" + esc(row.account.is_abnormal ? this.noteText(row.account) : "") + "</td>" +
              "</tr>"
            );
          }.bind(this)
        )
        .join("");

      const totalsRow =
        '<tr class="totals ' + (totals.is_balanced ? "ok" : "bad") + '">' +
        "<td></td>" +
        "<td>" + esc(this.$t("finance.trialBalance.totals")) + "</td>" +
        '<td class="num">' + esc(this.formatAmount(totals.debit_total)) + "</td>" +
        '<td class="num">' + esc(this.formatAmount(totals.credit_total)) + "</td>" +
        '<td class="num">' + esc(this.formatAmount(Math.abs(totals.difference))) + "</td>" +
        "<td>" +
        esc(
          totals.is_balanced
            ? this.$t("finance.trialBalance.totalsMatch")
            : this.$t("finance.trialBalance.totalsMismatch")
        ) +
        "</td>" +
        "<td>" + esc(totals.is_balanced ? "" : this.$t("finance.trialBalance.totalsMismatchWarning")) + "</td>" +
        "</tr>";

      const doc =
        "<!doctype html>" +
        '<html dir="' + (isArabic ? "rtl" : "ltr") + '" lang="' + (isArabic ? "ar" : "en") + '">' +
        '<head><meta charset="utf-8"><title>' + esc(title) + "</title><style>" +
        'body{font-family:"Segoe UI",Tahoma,Arial,sans-serif;color:#111;margin:0;padding:24px;}' +
        "header{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #111;padding-bottom:8px;margin-bottom:14px;}" +
        "h1{font-size:20px;margin:0;}" +
        ".meta{font-size:12px;color:#555;text-align:end;}" +
        "table{width:100%;border-collapse:collapse;font-size:12px;}" +
        "th,td{border:1px solid #888;padding:4px 8px;text-align:start;vertical-align:top;}" +
        "thead{display:table-header-group;}" +
        "thead th{background:#eee;font-weight:700;}" +
        "tr{page-break-inside:avoid;}" +
        ".lvl-1 td{font-weight:700;font-size:13px;background:#f3f3f3;}" +
        ".lvl-2 td{font-weight:700;}" +
        ".abnormal td{background:#ffe0b2;}" +
        ".totals td{font-weight:700;}" +
        ".totals.ok td{background:#e8f5e9;}" +
        ".totals.bad td{background:#ffcdd2;}" +
        ".code{white-space:nowrap;}" +
        ".num{text-align:end;direction:ltr;unicode-bidi:embed;white-space:nowrap;}" +
        "@page{size:A4;margin:12mm;}" +
        "</style></head><body>" +
        "<header><h1>" + esc(title) + '</h1><div class="meta">' +
        esc(this.periodLabel()) + "<br>" +
        esc(this.$t("finance.coa.exportedAt")) + ": " + esc(stamp) + "</div></header>" +
        "<table><thead><tr>" + headerCells + "</tr></thead><tbody>" + bodyRows + totalsRow + "</tbody></table>" +
        "</body></html>";

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.failedDownloadReport"), "red");
        return;
      }
      printWindow.document.write(doc);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(function () {
        printWindow.print();
      }, 400);
    },
  },
};
</script>

<style scoped>
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05); only the scroll height is page-specific. */
.tb-table-wrap {
  --fin-table-max-h: calc(100vh - 250px);
  min-height: 320px;
}
/* Below ~1620px the 9-field filter strip wraps to a second row; reserve the
   extra header height so the sticky totals row stays inside the viewport. */
@media (max-width: 1620px) {
  .tb-table-wrap {
    --fin-table-max-h: calc(100vh - 292px);
  }
}
.tb-col-notes {
  white-space: normal;
  min-width: 220px;
}
.tb-parent-row td {
  background: #f5f7fa;
}
/* Doc 4.1 visual rule: level 1 bold + slightly larger, level 2 bold. */
.tb-level-1 td {
  font-weight: 700;
  font-size: 14px;
}
.tb-level-2 td {
  font-weight: 700;
}
/* Abnormal balances stand out (doc: red or orange). */
.tb-abnormal-row td {
  background: #fff3e0;
}
.tb-abnormal-row .tb-note {
  color: #d84315;
  white-space: normal;
}
/* Rows kept only so the tree stays renderable (outside the filter). */
.tb-context-row td {
  color: rgba(0, 0, 0, 0.45);
}
.tb-inactive-row td {
  color: rgba(0, 0, 0, 0.4);
}
.tb-indent {
  display: inline-flex;
  align-items: center;
}
.tb-toggle {
  margin: 0;
  margin-inline-end: 4px;
  height: 22px;
  width: 22px;
}
.tb-toggle-spacer {
  display: inline-block;
  width: 26px;
}
.tb-side {
  font-weight: 600;
}
.tb-side-debit {
  color: #1565c0;
}
.tb-side-credit {
  color: #2e7d32;
}
.tb-side-balanced {
  color: rgba(0, 0, 0, 0.55);
}
.tb-match-ok {
  color: #2e7d32;
}
.tb-match-bad {
  color: #c62828;
}
@media print {
  .tb-no-print {
    display: none !important;
  }
}
</style>
